import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo, ThemeToggle, Footer } from "@/components/sg/Brand";

export function AuthFrame({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <div className="max-w-7xl w-full mx-auto px-6 py-4 flex items-center justify-between"><Logo /><ThemeToggle /></div>
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md border border-border rounded-xl bg-card p-8">
          <h1 className="font-display text-2xl font-bold">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1 mb-6">{subtitle}</p>}
          {children}
        </div>
      </div>
      <Footer className="mt-0" />
    </div>
  );
}

export function AuthPage() {
  const { login, register } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [f, setF] = useState({ email: "", password: "", display_name: "", company_name: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (mode === "login") { await login(f.email, f.password); toast.success("Welcome back"); nav("/app"); }
      else { await register(f); toast.success("Account created — welcome to ScopeGuard"); nav("/welcome"); }
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const isReg = mode === "register";
  return (
    <AuthFrame title={isReg ? "Create your free account" : "Sign in"} subtitle={isReg ? "2 projects free. No credit card." : "Pick up where you left off."}>
      <form onSubmit={submit} className="space-y-4">
        {isReg && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Your name</Label><Input data-testid="auth-display-name" value={f.display_name} onChange={set("display_name")} placeholder="Jamie Rivera" /></div>
            <div><Label>Company</Label><Input data-testid="auth-company-name" value={f.company_name} onChange={set("company_name")} placeholder="Rivera Remodeling" /></div>
          </div>
        )}
        <div><Label>Email</Label><Input data-testid="auth-email" type="email" required value={f.email} onChange={set("email")} /></div>
        <div>
          <div className="flex items-center justify-between">
            <Label>Password</Label>
            {!isReg && <Link to="/forgot-password" className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>}
          </div>
          <Input data-testid="auth-password" type="password" required minLength={isReg ? 8 : 1} value={f.password} onChange={set("password")} placeholder={isReg ? "At least 8 characters" : ""} />
        </div>
        <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full h-11 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold">
          {loading ? "Please wait…" : isReg ? "Create account" : "Sign in"}
        </Button>
      </form>
      <button className="mt-5 text-sm text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMode(isReg ? "login" : "register")} data-testid="auth-toggle-btn">
        {isReg ? "Already have an account? Sign in" : "New here? Create a free account"}
      </button>
      {isReg && <p className="mt-4 text-[11px] text-muted-foreground opacity-80">By creating an account you agree to the <Link to="/legal/terms" className="underline">Terms</Link> and <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.</p>}
    </AuthFrame>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <AuthFrame title="Reset your password" subtitle="We'll email you a secure link that works for 1 hour.">
      {sent ? (
        <div className="text-sm leading-relaxed" data-testid="forgot-sent-msg">If that email is registered, a reset link is on its way. Check your inbox (and spam folder).</div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Email</Label><Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <Button data-testid="forgot-submit-btn" disabled={loading} className="w-full h-11 bg-indigo-600 hover:bg-indigo-500 text-white">{loading ? "Sending…" : "Send reset link"}</Button>
        </form>
      )}
      <Link to="/auth" className="inline-block mt-5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-signin-link">← Back to sign in</Link>
    </AuthFrame>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const token = params.get("token") || "";
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/auth");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <AuthFrame title="Choose a new password" subtitle="Use at least 8 characters.">
      {!token ? <div className="text-sm">This link is missing its token. Request a new one from the sign-in page.</div> : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>New password</Label><Input data-testid="reset-password-input" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} /></div>
          <div><Label>Confirm password</Label><Input data-testid="reset-password-confirm-input" type="password" minLength={8} required value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
          <Button data-testid="reset-submit-btn" disabled={loading} className="w-full h-11 bg-indigo-600 hover:bg-indigo-500 text-white">{loading ? "Saving…" : "Update password"}</Button>
        </form>
      )}
      <Link to="/auth" className="inline-block mt-5 text-sm text-muted-foreground hover:text-foreground">← Back to sign in</Link>
    </AuthFrame>
  );
}
